import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import MainNavigation from '../../components/ui/MainNavigation';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import RFQCard from './components/RFQCard';
import RFQDetails from './components/RFQDetails';
import FilterPanel from './components/FilterPanel';
import QuoteGenerator from './components/QuoteGenerator';
import StatsOverview from './components/StatsOverview';

const RFQProcessing = () => {
  const [selectedRFQ, setSelectedRFQ] = useState(null);
  const [showQuoteGenerator, setShowQuoteGenerator] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    priority: '',
    category: '',
    assignee: '',
    sortBy: 'newest',
    dateFrom: '',
    dateTo: '',
    minValue: '',
    maxValue: ''
  });

  // Mock user data
  const currentUser = {
    id: 'admin_1',
    name: 'Sarah Johnson',
    email: 'sarah.johnson@techtribes.ug',
    role: 'admin'
  };

  // Mock RFQ data
  const mockRFQs = [
    {
      id: 'RFQ001',
      title: 'Enterprise Network Infrastructure Setup',
      description: `We require a comprehensive network infrastructure solution for our new office building. The setup should include:\n\n• Structured cabling for 200+ workstations\n• Core network switches and routers\n• Wireless access points for complete coverage\n• Network security appliances\n• Server room setup with proper cooling\n• Backup and disaster recovery solutions\n\nThe project timeline is critical as we plan to move operations by Q1 2025.`,
      customer: {
        id: 'cust_001',
        name: 'James Mukasa',
        email: 'james.mukasa@innovateug.com',
        company: 'Innovate Uganda Ltd',
        phone: '+256 700 123 456'
      },
      category: 'infrastructure',
      priority: 'high',
      status: 'pending',
      createdAt: new Date('2024-12-15T09:30:00'),
      deadline: new Date('2024-12-25T17:00:00'),
      estimatedValue: 150000000,
      budgetRange: {
        min: 120000000,
        max: 180000000
      },
      timeline: '6-8 weeks',
      assignedTo: null,
      requirements: [
        {
          title: 'Network Infrastructure',
          description: 'Complete structured cabling and network equipment',
          specifications: [
            'Cat 6A cabling for 200 workstations',
            'Core switches with 48-port capacity',
            'Enterprise-grade routers',
            'Managed switches for each floor'
          ]
        },
        {
          title: 'Wireless Network',
          description: 'Comprehensive WiFi coverage',
          specifications: [
            'WiFi 6 access points',
            'Centralized wireless controller',
            'Guest network isolation',
            'Coverage for 5000 sq meters'
          ]
        }
      ],
      history: [
        {
          type: 'status',
          message: 'RFQ submitted by customer',
          user: 'James Mukasa',
          timestamp: new Date('2024-12-15T09:30:00')
        },
        {
          type: 'note',
          message: 'Initial review completed. Requires site visit for accurate assessment.',
          user: 'Sarah Johnson',
          timestamp: new Date('2024-12-15T14:20:00')
        }
      ],
      documents: [
        {
          name: 'Building Floor Plans.pdf',
          size: '2.4 MB',
          uploadedAt: new Date('2024-12-15T09:30:00')
        },
        {
          name: 'Current Network Diagram.docx',
          size: '856 KB',
          uploadedAt: new Date('2024-12-15T09:35:00')
        }
      ]
    },
    {
      id: 'RFQ002',
      title: 'Custom ERP System Development',
      description: `Looking for a custom ERP solution tailored to our manufacturing processes. Key requirements include:\n\n• Inventory management with real-time tracking\n• Production planning and scheduling\n• Financial management integration\n• HR and payroll modules\n• Customer relationship management\n• Reporting and analytics dashboard\n\nThe system should be web-based and mobile-responsive for our field operations.`,
      customer: {
        id: 'cust_002',
        name: 'Grace Namugga',
        email: 'grace.namugga@manufacturingplus.ug',
        company: 'Manufacturing Plus Uganda',
        phone: '+256 701 234 567'
      },
      category: 'software',
      priority: 'medium',
      status: 'in_progress',
      createdAt: new Date('2024-12-10T11:15:00'),
      deadline: new Date('2024-12-30T17:00:00'),
      estimatedValue: 85000000,
      budgetRange: {
        min: 70000000,
        max: 100000000
      },
      timeline: '12-16 weeks',
      assignedTo: 'mike_wilson',
      requirements: [
        {
          title: 'Core ERP Modules',
          description: 'Essential business process modules',
          specifications: [
            'Inventory management system',
            'Production planning module',
            'Financial accounting integration',
            'Purchase order management'
          ]
        }
      ],
      history: [
        {
          type: 'status',
          message: 'RFQ assigned to Mike Wilson for technical analysis',
          user: 'Sarah Johnson',
          timestamp: new Date('2024-12-11T10:00:00')
        }
      ],
      documents: [
        {
          name: 'Business Requirements.pdf',
          size: '1.8 MB',
          uploadedAt: new Date('2024-12-10T11:15:00')
        }
      ]
    },
    {
      id: 'RFQ003',
      title: 'IT Security Audit and Compliance',
      description: `We need a comprehensive IT security audit to ensure compliance with international standards. The scope includes:\n\n• Network security assessment\n• Vulnerability testing and penetration testing\n• Policy review and recommendations\n• Staff training on cybersecurity\n• Compliance with ISO 27001 standards\n• Implementation of security measures\n\nThis is urgent due to upcoming regulatory requirements.`,
      customer: {
        id: 'cust_003',
        name: 'Robert Ssemakula',
        email: 'robert.ssemakula@financecorp.ug',
        company: 'Finance Corporation Uganda',
        phone: '+256 702 345 678'
      },
      category: 'consultancy',
      priority: 'high',
      status: 'quoted',
      createdAt: new Date('2024-12-05T14:45:00'),
      deadline: new Date('2024-12-20T17:00:00'),
      estimatedValue: 45000000,
      budgetRange: {
        min: 35000000,
        max: 55000000
      },
      timeline: '4-6 weeks',
      assignedTo: 'jane_smith',
      requirements: [
        {
          title: 'Security Assessment',
          description: 'Comprehensive security evaluation',
          specifications: [
            'Network vulnerability scanning',
            'Penetration testing',
            'Security policy review',
            'Compliance gap analysis'
          ]
        }
      ],
      history: [
        {
          type: 'status',
          message: 'Quote generated and sent to customer',
          user: 'Jane Smith',
          timestamp: new Date('2024-12-12T16:30:00')
        }
      ],
      documents: [
        {
          name: 'Current Security Policies.pdf',
          size: '3.2 MB',
          uploadedAt: new Date('2024-12-05T14:45:00')
        }
      ]
    }
  ];

  const [rfqs, setRFQs] = useState(mockRFQs);
  const [filteredRFQs, setFilteredRFQs] = useState(mockRFQs);

  // Mock stats data
  const statsData = {
    total: 156,
    pending: 23,
    inProgress: 18,
    quoted: 12,
    conversionRate: 68,
    avgResponseTime: 4.2
  };

  const rfqCounts = {
    pending: rfqs?.filter(rfq => rfq?.status === 'pending')?.length,
    in_progress: rfqs?.filter(rfq => rfq?.status === 'in_progress')?.length,
    quoted: rfqs?.filter(rfq => rfq?.status === 'quoted')?.length,
    rejected: rfqs?.filter(rfq => rfq?.status === 'rejected')?.length
  };

  // Filter RFQs based on current filters
  useEffect(() => {
    let filtered = [...rfqs];

    // Search filter
    if (filters?.search) {
      const searchTerm = filters?.search?.toLowerCase();
      filtered = filtered?.filter(rfq =>
        rfq?.title?.toLowerCase()?.includes(searchTerm) ||
        rfq?.customer?.name?.toLowerCase()?.includes(searchTerm) ||
        rfq?.customer?.company?.toLowerCase()?.includes(searchTerm) ||
        rfq?.id?.toLowerCase()?.includes(searchTerm)
      );
    }

    // Status filter
    if (filters?.status) {
      filtered = filtered?.filter(rfq => rfq?.status === filters?.status);
    }

    // Priority filter
    if (filters?.priority) {
      filtered = filtered?.filter(rfq => rfq?.priority === filters?.priority);
    }

    // Category filter
    if (filters?.category) {
      filtered = filtered?.filter(rfq => rfq?.category === filters?.category);
    }

    // Assignee filter
    if (filters?.assignee) {
      if (filters?.assignee === 'unassigned') {
        filtered = filtered?.filter(rfq => !rfq?.assignedTo);
      } else {
        filtered = filtered?.filter(rfq => rfq?.assignedTo === filters?.assignee);
      }
    }

    // Date range filter
    if (filters?.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      filtered = filtered?.filter(rfq => new Date(rfq.createdAt) >= fromDate);
    }
    if (filters?.dateTo) {
      const toDate = new Date(filters.dateTo);
      filtered = filtered?.filter(rfq => new Date(rfq.createdAt) <= toDate);
    }

    // Value range filter
    if (filters?.minValue) {
      filtered = filtered?.filter(rfq => (rfq?.estimatedValue || 0) >= parseInt(filters?.minValue));
    }
    if (filters?.maxValue) {
      filtered = filtered?.filter(rfq => (rfq?.estimatedValue || 0) <= parseInt(filters?.maxValue));
    }

    // Sort
    filtered?.sort((a, b) => {
      switch (filters?.sortBy) {
        case 'oldest':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'priority':
          const priorityOrder = { high: 3, medium: 2, low: 1 };
          return priorityOrder?.[b?.priority] - priorityOrder?.[a?.priority];
        case 'deadline':
          return new Date(a.deadline) - new Date(b.deadline);
        case 'value':
          return (b?.estimatedValue || 0) - (a?.estimatedValue || 0);
        default: // newest
          return new Date(b.createdAt) - new Date(a.createdAt);
      }
    });

    setFilteredRFQs(filtered);
  }, [filters, rfqs]);

  const handleStatusUpdate = (rfqId, newStatus) => {
    setRFQs(prev => prev?.map(rfq =>
      rfq?.id === rfqId
        ? {
            ...rfq,
            status: newStatus,
            history: [
              ...rfq?.history,
              {
                type: 'status',
                message: `Status changed to ${newStatus?.replace('_', ' ')}`,
                user: currentUser?.name,
                timestamp: new Date()
              }
            ]
          }
        : rfq
    ));

    if (selectedRFQ && selectedRFQ?.id === rfqId) {
      setSelectedRFQ(prev => ({
        ...prev,
        status: newStatus
      }));
    }
  };

  const handleAssignRFQ = (rfqId, assignee) => {
    setRFQs(prev => prev?.map(rfq =>
      rfq?.id === rfqId
        ? {
            ...rfq,
            assignedTo: assignee,
            history: [
              ...rfq?.history,
              {
                type: 'assignment',
                message: `RFQ assigned to ${assignee}`,
                user: currentUser?.name,
                timestamp: new Date()
              }
            ]
          }
        : rfq
    ));

    if (selectedRFQ && selectedRFQ?.id === rfqId) {
      setSelectedRFQ(prev => ({
        ...prev,
        assignedTo: assignee
      }));
    }
  };

  const handleAddNote = (rfqId, note) => {
    const newHistoryItem = {
      type: 'note',
      message: note,
      user: currentUser?.name,
      timestamp: new Date()
    };

    setRFQs(prev => prev?.map(rfq =>
      rfq?.id === rfqId
        ? {
            ...rfq,
            history: [...rfq?.history, newHistoryItem]
          }
        : rfq
    ));

    if (selectedRFQ && selectedRFQ?.id === rfqId) {
      setSelectedRFQ(prev => ({
        ...prev,
        history: [...prev?.history, newHistoryItem]
      }));
    }
  };

  const handleGenerateQuote = (quoteData) => {
    console.log('Generated quote:', quoteData);
    handleStatusUpdate(selectedRFQ?.id, 'quoted');
    setShowQuoteGenerator(false);
    // In a real app, this would save the quote and send it to the customer
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      priority: '',
      category: '',
      assignee: '',
      sortBy: 'newest',
      dateFrom: '',
      dateTo: '',
      minValue: '',
      maxValue: ''
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation user={currentUser} />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <BreadcrumbNavigation />
          
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">RFQ Processing</h1>
              <p className="text-muted-foreground">Manage and process customer quotation requests</p>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                iconName={showFilters ? "EyeOff" : "Eye"}
                iconPosition="left"
              >
                {showFilters ? 'Hide' : 'Show'} Filters
              </Button>
              <Link to="/admin-dashboard">
                <Button variant="outline" iconName="ArrowLeft" iconPosition="left">
                  Back to Dashboard
                </Button>
              </Link>
            </div>
          </div>

          {/* Stats Overview */}
          <StatsOverview stats={statsData} />

          {/* Main Content */}
          <div className="grid grid-cols-12 gap-6">
            {/* Filters Panel */}
            {showFilters && (
              <div className="col-span-12 lg:col-span-3">
                <FilterPanel
                  filters={filters}
                  onFilterChange={setFilters}
                  onClearFilters={clearFilters}
                  rfqCounts={rfqCounts}
                />
              </div>
            )}

            {/* RFQ List */}
            <div className={`col-span-12 ${showFilters ? 'lg:col-span-4' : 'lg:col-span-5'}`}>
              <div className="bg-card border border-border rounded-lg">
                <div className="p-4 border-b border-border">
                  <div className="flex items-center justify-between">
                    <h2 className="font-semibold text-foreground">
                      RFQ List ({filteredRFQs?.length})
                    </h2>
                    <Button variant="outline" size="sm" iconName="RefreshCw">
                      Refresh
                    </Button>
                  </div>
                </div>
                
                <div className="max-h-[calc(100vh-400px)] overflow-y-auto">
                  {filteredRFQs?.length > 0 ? (
                    <div className="p-4 space-y-3">
                      {filteredRFQs?.map((rfq) => (
                        <RFQCard
                          key={rfq?.id}
                          rfq={rfq}
                          onSelect={setSelectedRFQ}
                          isSelected={selectedRFQ?.id === rfq?.id}
                          onStatusChange={handleStatusUpdate}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="p-8 text-center">
                      <Icon name="Search" size={48} className="mx-auto text-muted-foreground mb-4" />
                      <h3 className="text-lg font-semibold text-foreground mb-2">No RFQs Found</h3>
                      <p className="text-muted-foreground">Try adjusting your filters to see more results</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* RFQ Details */}
            <div className={`col-span-12 ${showFilters ? 'lg:col-span-5' : 'lg:col-span-7'}`}>
              <div className="relative">
                <RFQDetails
                  rfq={selectedRFQ}
                  onStatusUpdate={handleStatusUpdate}
                  onAssign={handleAssignRFQ}
                  onAddNote={handleAddNote}
                />
                
                {/* Quick Actions */}
                {selectedRFQ && (
                  <div className="absolute top-4 right-4 flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowQuoteGenerator(true)}
                      iconName="FileText"
                      iconPosition="left"
                    >
                      Generate Quote
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Quote Generator Modal */}
      {showQuoteGenerator && selectedRFQ && (
        <QuoteGenerator
          rfq={selectedRFQ}
          onGenerateQuote={handleGenerateQuote}
          onClose={() => setShowQuoteGenerator(false)}
        />
      )}
    </div>
  );
};

export default RFQProcessing;